import React from 'react';
import { useUser } from '../../../redux/user/useUser';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import media from 'styled-media-query';

import { BasicLayout } from '../../templates/BasicLayout';
import { Button } from '../../atoms/Button';
import { Select } from '../../atoms/Select';
import { TextArea, TextAreaHandler } from '../../atoms/TextArea';
import { Loader } from '../../molecules/Loader';

import {
  useGetFeedbackCategoriesQuery,
  useCreateProgramFeedbackMutation,
  useGetProgramQuery,
} from '../../../gen/graphql';
import { useToastsContext } from '../../../context/ToastsProvider';
import { ErrorMessage } from '../../../const/ErrorMessage';
import { FunctionType, PermissionType } from '../../../const/UserPermission';
import { NotFoundPage } from '../public/NotFound';
import { LOWER_META_TITLE } from '../../../const/Service';

export const ProgramFeedbackContainer = (): JSX.Element => {
  const params = useParams<{ program_id: string; chapter_id: string }>();
  const pathProgramID = Number(params.program_id);
  const pathChapterID = Number(params.chapter_id);
  if (Number.isNaN(pathProgramID) || Number.isNaN(pathChapterID)) return NotFoundPage;

  return <ProgramFeedback programID={pathProgramID} chapterID={pathChapterID} />;
};

interface ProgramFeedbackProps {
  programID: number;
  chapterID: number;
}

const ProgramFeedback = (props: ProgramFeedbackProps): JSX.Element => {
  const metaTitle = `ご意見・ご要望 | ${LOWER_META_TITLE}`;

  const [showLoader, setShowLoader] = React.useState(false);
  const { showToast } = useToastsContext();
  const sending = React.useRef(false);
  const [category, setCategory] = React.useState('0');
  const [categoryName, setCategoryName] = React.useState('');
  const [content, setContent] = React.useState('');
  const [createProgramFeedback] = useCreateProgramFeedbackMutation();
  const [submitted, setSubmitted] = React.useState(false);
  const navigate = useNavigate();

  const { user, permissionCheck } = useUser();
  const { data } = useGetFeedbackCategoriesQuery({
    fetchPolicy: 'no-cache',
    variables: {
      first: 10,
      after: 0,
    },
  });

  const contentRef = React.useRef<TextAreaHandler>(null);

  const { data: programData } = useGetProgramQuery({
    variables: {
      programID: props.programID,
      userID: user.lmsUser?.id,
    },
    onError: () => {
      // 不正なidの場合教材トップにリダイレクト
      return <Navigate to="/programs" replace />;
    },
  });
  const program = programData?.program;

  const submit = async () => {
    if (sending.current || !contentRef.current) {
      return;
    }

    if (category === '0') {
      showToast(1, 'カテゴリーを選択してください');
      return;
    }

    if (contentRef.current.getValue() === '') {
      showToast(1, '本文を入力してください');
      return;
    }

    sending.current = true;
    setShowLoader(true);

    try {
      await createProgramFeedback({
        variables: {
          input: {
            chapterID: props.chapterID,
            feedbackCategoryID: parseInt(category),
            content: contentRef.current.getValue(),
          },
        },
      });
      setContent(contentRef.current.getValue());
      setSubmitted(true);
    } catch (e) {
      // GraphQLのエラーは共通のエラーハンドラでSentryに送信しているためここでは握りつぶす
      showToast(1, ErrorMessage);
    } finally {
      sending.current = false;
      setShowLoader(false);
    }
  };

  return (
    <>
      <BasicLayout pageTitle="教材" metaTitle={metaTitle}>
        <Container>
          <Breadcrumb>
            <StyledLink
              to={
                permissionCheck(FunctionType.ProgramForInstructorAndCoach, PermissionType.Read)
                  ? '/programs?type=1'
                  : '/programs'
              }
            >
              教材TOP
            </StyledLink>
            <StyledLink to={program?.id ? `/programs/${program.id}/chapters` : ''}>
              {program?.title}
            </StyledLink>
            <Current>ご意見・ご要望</Current>
          </Breadcrumb>
          <Form>
            <h3>
              {submitted
                ? 'ありがとうございます。下記の内容でご意見・ご要望を承りました'
                : 'この章へのご意見・ご要望をお聞かせください'}
            </h3>
            {submitted && (
              <Thanks>
                入力した内容でご意見・ご要望を承りました。頂いたご要望を元に改善させていただきます。
              </Thanks>
            )}
            <WithLabel>
              <div className="label">
                <p>カテゴリー</p>
              </div>
              <div className="content">
                {submitted ? (
                  <p>{categoryName}</p>
                ) : (
                  <Select
                    name="category"
                    onChange={(e) => {
                      setCategory(e.target.value);

                      if (!data) return;

                      const newCategory = data.feedbackCategories.items.find(
                        (item) => item.id === parseInt(e.target.value),
                      );
                      setCategoryName(newCategory?.name ? newCategory.name : '');
                    }}
                    options={
                      data
                        ? [
                            { name: '選択してください', value: '0' },
                            ...data.feedbackCategories.items.map((item) => {
                              return {
                                name: item.name,
                                value: item.id.toString(),
                              };
                            }),
                          ]
                        : []
                    }
                    value={category}
                  />
                )}
              </div>
            </WithLabel>
            <WithLabel>
              <div className="label">
                <p>ご意見・ご要望</p>
              </div>
              <div className="content">
                {submitted ? <p>{content}</p> : <StyledTextArea name="content" ref={contentRef} />}
              </div>
            </WithLabel>
            {submitted ? (
              <Submit
                onClick={() => navigate(`/programs/${props.programID}/chapters/${props.chapterID}`)}
                border
              >
                <p>教材に戻る</p>
              </Submit>
            ) : (
              <>
                <Submit onClick={submit}>
                  <p>送信</p>
                </Submit>
                <AttentionNote>
                  <p>
                    ※個別の返信はしておりません。返信が必要な場合は、右下のチャットルームにてカスタマーサポートにご連絡ください。
                  </p>
                </AttentionNote>
              </>
            )}
          </Form>
        </Container>
      </BasicLayout>
      <Loader display={showLoader} />
    </>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1100px;
  padding: 24px 126px;
  box-sizing: border-box;

  ${media.lessThan('medium')`
    flex-direction: column;
    padding: 2rem 0;
  `}
`;

const Breadcrumb = styled.div`
  width: 100%;
  margin: 0 auto 2rem;
  line-height: 0.9375rem;
  box-sizing: border-box;

  & > * + * {
    &:before {
      content: '>';
      margin: 0 0.3em;
      color: rgba(0, 0, 0, 0.6);
    }
  }

  ${media.lessThan('medium')`
    padding: 0 16px;
  `}
`;

const StyledLink = styled(Link)`
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.8125rem;
  transition: all 0.2s;

  &:hover {
    color: #e2001b;
    text-decoration: underline;
  }
`;

const Current = styled.span`
  color: #eb0000;
  font-size: 0.8125rem;
`;

const Form = styled.section`
  width: 788px;
  padding: 32px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.25rem;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.87);
    margin-bottom: 48px;
  }

  ${media.lessThan('medium')`
    width: 100%;
    padding: 32px 16px;
    box-sizing: border-box;

    h3 {
      font-size: 1.25rem;
      line-height: 1.5;
      margin-bottom: 24px;
    }
  `}
`;

const WithLabel = styled.div`
  display: flex;
  align-items: flex-start;

  & + * {
    margin-top: 24px;
  }

  .label {
    width: 192px;
    min-width: 192px;
    padding-top: 0.5rem;

    p {
      font-size: 1rem;
      font-weight: bold;
      color: rgba(0, 0, 0, 0.87);
    }
  }

  .content {
    width: 100%;

    p {
      padding: 0.5rem;
      white-space: pre-wrap;
    }
  }

  ${media.lessThan('medium')`
    flex-direction: column;

    .content {
      margin-top: 8px;
    }
  `}
`;

const Submit = styled(Button)`
  display: block;
  width: 100%;
  max-width: 524px;
  margin: 2rem auto 0;
`;

const AttentionNote = styled.span`
  color: rgba(0, 0, 0, 0.87);

  p {
    margin-top: 2rem;
    font-size: 0.75rem;
    line-height: 1.2em;
  }
`;

const StyledTextArea = styled(TextArea)`
  width: 520px;
  height: 200px;

  ${media.lessThan('medium')`
    width: 100%;
    height: 140px;
  `}
`;

const Thanks = styled.p`
  font-size: 0.8rem;
  margin: 48px 0 24px 0;
  line-height: 1.6;

  ${media.lessThan('medium')`
    margin: 12px 0;
  `}
`;
